import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Hand, Users, X, Loader2, Settings } from 'lucide-react';
import { TeamsBoard } from './CalendarTeams';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TZ = 'Europe/Oslo';
const todayStr = () => new Date().toLocaleDateString('sv-SE', { timeZone: TZ });

export const canAttend = (event) => !!event?.occurrence_date && event.attendance_enabled !== false && event.occurrence_date >= todayStr();

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', timeZone: TZ });
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : `${d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', timeZone: TZ }).replace(/\.$/, '')} kl. ${fmtTime(iso)}`;
};

// «Jeg kommer» / «Deltakelse innmeldt» med bekreftelse ved avmelding
export const AttendButton = ({ event, onChanged, size = 'md' }) => {
  const [busy, setBusy] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const attending = !!event.i_am_attending;
  const id = `${event.attendance_event_id}-${event.occurrence_date}`;
  const h = size === 'xs' ? 'w-full min-h-[28px] px-1 py-1 text-[10px] leading-tight' : size === 'sm' ? 'h-9 px-3 text-xs' : 'h-11 px-4 text-sm';

  const toggle = async (e) => {
    e.stopPropagation();
    if (attending && !confirmOff) { setConfirmOff(true); return; }
    setBusy(true);
    try {
      const r = attending
        ? await axios.delete(`${API_BASE}/api/calendar/attendance`, { ...auth(), params: { event_id: event.attendance_event_id, occurrence_date: event.occurrence_date } })
        : await axios.post(`${API_BASE}/api/calendar/attendance`, {
            event_id: event.attendance_event_id,
            occurrence_date: event.occurrence_date,
            event_title: event.title,
            event_start: event.start_datetime,
            event_location: event.location,
          }, auth());
      const patch = { i_am_attending: r.data.attending, attendance_count: r.data.count };
      if (event.capacity) patch.capacity = { ...event.capacity, my_team: r.data.attending ? r.data.team : null };
      onChanged(event, patch);
      toast.success(r.data.attending ? (r.data.team ? `Deltakelse innmeldt – du er på Lag ${r.data.team}` : 'Deltakelse innmeldt') : 'Du er meldt av');
      window.dispatchEvent(new Event('calendarRefresh'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke oppdatere påmelding');
    } finally {
      setBusy(false);
      setConfirmOff(false);
    }
  };

  if (confirmOff) {
    return (
      <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()} data-testid={`attend-confirm-${id}`}>
        <span className="text-xs text-gray-200 whitespace-nowrap">Meld av?</span>
        <button type="button" onClick={toggle} disabled={busy}
          className={`${h} rounded-lg bg-red-500/20 text-red-300 border border-red-500/40 font-semibold whitespace-nowrap touch-manipulation`}
          data-testid={`attend-confirm-yes-${id}`}>Ja, meld av</button>
        <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmOff(false); }}
          className={`${h} rounded-lg bg-gray-700 text-gray-200 whitespace-nowrap touch-manipulation`}
          data-testid={`attend-confirm-no-${id}`}>Avbryt</button>
      </div>
    );
  }

  return (
    <button type="button" onClick={toggle} disabled={busy}
      className={`${h} inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold touch-manipulation transition-colors ${attending
        ? 'bg-green-600 text-white hover:bg-green-500'
        : 'bg-yellow-500 text-gray-900 hover:bg-yellow-400'}`}
      title={attending ? 'Du er påmeldt. Trykk igjen for å melde deg av.' : 'Trykk for å melde at du kommer, så skyteleder og andre medlemmer vet hvem som kommer.'}
      data-testid={`attend-btn-${id}`} data-attending={attending}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : attending ? <Check className={`${size === 'xs' ? 'w-3 h-3 hidden sm:block' : 'w-4 h-4'} shrink-0`} /> : <Hand className={`${size === 'xs' ? 'w-3 h-3 hidden sm:block' : 'w-4 h-4'} shrink-0`} />}
      {attending ? (size === 'xs' ? <><span className="sm:hidden">Innmeldt</span><span className="hidden sm:inline">Deltakelse innmeldt</span></> : 'Deltakelse innmeldt') : 'Jeg kommer'}
    </button>
  );
};

// Antall påmeldte – klikkbart for Admin/Skyteleder
export const AttendCount = ({ event, canView, onOpen }) => {
  const n = event.attendance_count || 0;
  const id = `${event.attendance_event_id}-${event.occurrence_date}`;
  const label = `${n} ${n === 1 ? 'deltaker' : 'deltakere'}`;
  if (!canView) {
    return <span className="text-xs text-gray-300 inline-flex items-center gap-1" title="Antall som har meldt at de kommer." data-testid={`attend-count-${id}`}><Users className="w-3.5 h-3.5" />{label}</span>;
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(event); }}
      className="text-xs text-blue-300 hover:text-blue-200 underline underline-offset-2 inline-flex items-center gap-1 min-h-[36px] touch-manipulation"
      title="Trykk for å se hvem som kommer." data-testid={`attend-count-${id}`}>
      <Users className="w-3.5 h-3.5" />{label}
    </button>
  );
};

// Popup med liste over påmeldte. Enten for én hendelse (event) eller dagens (today)
export const AttendeesModal = ({ event, today, onClose, canManage = false }) => {
  const [rows, setRows] = useState(null);
  const [capEvents, setCapEvents] = useState([]);
  const showTeams = !today && canManage && !!event?.capacity;

  useEffect(() => {
    if (showTeams) return;
    const req = today
      ? axios.get(`${API_BASE}/api/calendar/attendance/today`, auth())
      : axios.get(`${API_BASE}/api/calendar/attendance/event`, { ...auth(), params: { event_id: event.attendance_event_id, occurrence_date: event.occurrence_date } });
    req.then(r => { setRows(r.data.attendees || []); setCapEvents(r.data.capacity_events || []); }).catch(() => setRows([]));
  }, [event, today, showTeams]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4" onClick={onClose} data-testid="attendees-modal">
      <div className="bg-gray-800 w-full sm:max-w-md rounded-t-2xl sm:rounded-xl max-h-[85vh] flex flex-col pb-[env(safe-area-inset-bottom)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-700">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-white">{today ? 'Påmeldt i dag' : showTeams ? 'Lagoversikt' : 'Hvem kommer'}</h3>
            {!today && <p className="text-sm text-gray-400 truncate">{event.title} · {new Date(event.start_datetime).toLocaleDateString('nb-NO', { timeZone: TZ })}</p>}
          </div>
          <button type="button" onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-700" data-testid="attendees-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-4 space-y-2">
          {showTeams && <TeamsBoard event={event} />}
          {today && capEvents.map(ce => (
            <div key={`${ce.attendance_event_id}-${ce.occurrence_date}`} className="pb-3 mb-1 border-b border-gray-700" data-testid={`today-teams-${ce.attendance_event_id}`}>
              <p className="text-white font-semibold mb-2">{ce.title}{ce.start_datetime ? ` kl. ${fmtTime(ce.start_datetime)}` : ''}</p>
              <TeamsBoard event={ce} />
            </div>
          ))}
          {today && capEvents.length > 0 && rows && rows.some(r => !capEvents.some(ce => ce.attendance_event_id === r.event_id)) && (
            <p className="text-sm text-gray-400 pt-1">Andre aktiviteter i dag:</p>
          )}
          {!showTeams && rows === null && <p className="text-gray-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Laster…</p>}
          {rows && rows.length === 0 && capEvents.length === 0 && <p className="text-gray-400 text-sm" data-testid="attendees-empty">Ingen har meldt seg på ennå.</p>}
          {rows && rows.filter(r => !(today && capEvents.some(ce => ce.attendance_event_id === r.event_id))).map((r, i) => (
            <div key={r.id || i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-700/50" data-testid={`attendee-row-${i}`}>
              <span className="w-8 h-8 shrink-0 rounded-full bg-green-600/30 text-green-200 flex items-center justify-center text-sm font-bold">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="text-white font-medium truncate">{r.user_name}</p>
                <p className="text-xs text-gray-400 truncate">
                  {today && <>{r.event_title}{r.event_start ? ` kl. ${fmtTime(r.event_start)}` : ''}{r.event_location ? ` · ${r.event_location}` : ''} · </>}
                  {r.team ? <span className="text-green-300 font-medium">Lag {r.team} · </span> : null}
                  {r.created_at ? `meldt ${fmtDateTime(r.created_at)}` : 'Kommer'}
                </p>
              </div>
            </div>
          ))}
        </div>
        {!showTeams && rows && rows.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-700 text-sm text-gray-300 flex items-center justify-between gap-2" data-testid="attendees-total">
            <span>{capEvents.length === 0 && <>Totalt: <strong className="text-white">{rows.length}</strong> påmeldt{rows.length === 1 ? '' : 'e'}</>}</span>
            {today && (
              <a href="/admin?tab=settings#kapasitet" title="Innstillinger for kapasitetsstyring og baner"
                className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-700"
                data-testid="today-capacity-settings-link"><Settings className="w-5 h-5" /></a>
            )}
          </div>
        )}
        {today && rows && rows.length === 0 && (
          <div className="px-4 py-3 border-t border-gray-700 flex justify-end">
            <a href="/admin?tab=settings#kapasitet" title="Innstillinger for kapasitetsstyring og baner"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-700"
              data-testid="today-capacity-settings-link"><Settings className="w-5 h-5" /></a>
          </div>
        )}
      </div>
    </div>
  );
};
